import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { parseAsString, useQueryState } from "nuqs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { FilterField, FilterTag, FilterOperator } from "@/components/admin/tickets/ColumnFilters";

export const TICKETS_QUERY_KEY = "admin-tickets";
export const TICKETS_PER_PAGE = 20;

export type TicketSortField = "created_at" | "event" | "guest" | "total_price" | "quantity" | "status";
export type SortDirection = "asc" | "desc";

const FIELD_LABELS: Record<FilterField, string> = {
  status: "Status",
  event: "Event",
  name: "Name",
  email: "Email",
  phone: "Phone",
  userId: "User ID",
  payment: "Payment",
  price: "Price",
  quantity: "Quantity",
};

const TICKET_FILTERS_STORAGE_KEY = "admin-ticket-filters";
const TICKET_SORT_STORAGE_KEY = "admin-ticket-sort";

const getInitialTicketFilters = (): string | null => {
  if (typeof window !== "undefined") {
    return localStorage.getItem(TICKET_FILTERS_STORAGE_KEY) || null;
  }
  return null;
};

const getInitialTicketSort = (): { field: TicketSortField; direction: SortDirection } => {
  if (typeof window !== "undefined") {
    const saved = localStorage.getItem(TICKET_SORT_STORAGE_KEY);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Fall through
      }
    }
  }
  return { field: "created_at", direction: "desc" };
};

// Helper to compare values with operators
const compareWithOperator = (value: number, filterValue: number, operator: FilterOperator): boolean => {
  switch (operator) {
    case "equals": return value === filterValue;
    case ">=": return value >= filterValue;
    case "<=": return value <= filterValue;
    case ">": return value > filterValue;
    case "<": return value < filterValue;
    default: return value === filterValue;
  }
};

// Helper to compare strings with operators
const compareStringWithOperator = (value: string, filterValue: string, operator: FilterOperator): boolean => {
  switch (operator) {
    case "equals": return value.toLowerCase() === filterValue.toLowerCase();
    case "contains": return value.toLowerCase().includes(filterValue.toLowerCase());
    default: return value.toLowerCase().includes(filterValue.toLowerCase());
  }
};

export const useAdminTickets = () => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const initialSort = getInitialTicketSort();
  const [sortField, setSortField] = useState<TicketSortField>(initialSort.field);
  const [sortDirection, setSortDirection] = useState<SortDirection>(initialSort.direction);

  // Filters stored as JSON string in URL with localStorage fallback
  const [filtersParam, setFiltersParamRaw] = useQueryState<string>(
    "ticketFilters",
    parseAsString.withOptions({ clearOnDefault: false })
  );

  // Wrapper to persist to localStorage
  const setFiltersParam = useCallback((value: string | null) => {
    if (value) {
      localStorage.setItem(TICKET_FILTERS_STORAGE_KEY, value);
    } else {
      localStorage.removeItem(TICKET_FILTERS_STORAGE_KEY);
    }
    setFiltersParamRaw(value);
  }, [setFiltersParamRaw]);

  // Initialize filters from localStorage on mount (only once)
  useEffect(() => {
    if (filtersParam === null) {
      const stored = getInitialTicketFilters();
      if (stored) {
        setFiltersParamRaw(stored);
      }
    }
  }, []); // Only run on mount

  // Parse filters from URL param into FilterTag array
  const filterTags = useMemo<FilterTag[]>(() => {
    if (!filtersParam) return [];
    try {
      const parsed = JSON.parse(filtersParam) as { field: FilterField; value: string; operator?: FilterOperator }[];
      return parsed.map((f, i) => ({
        id: `${f.field}-${i}-${f.value}-${f.operator || "default"}`,
        field: f.field,
        value: f.value,
        label: FIELD_LABELS[f.field],
        operator: f.operator,
      }));
    } catch {
      return [];
    }
  }, [filtersParam]);

  // Add a new filter tag
  const addFilter = useCallback((field: FilterField, value: string, operator?: FilterOperator) => {
    const current = filterTags.map(t => ({ field: t.field, value: t.value, operator: t.operator }));
    // Check if this exact filter already exists
    const exists = current.some(f => f.field === field && f.value.toLowerCase() === value.toLowerCase() && f.operator === operator);
    if (exists) return;
    const updated = [...current, { field, value, operator }];
    setFiltersParam(JSON.stringify(updated));
  }, [filterTags, setFiltersParam]);

  // Remove a filter tag by id
  const removeFilter = useCallback((id: string) => {
    const updated = filterTags
      .filter(t => t.id !== id)
      .map(t => ({ field: t.field, value: t.value, operator: t.operator }));
    setFiltersParam(updated.length > 0 ? JSON.stringify(updated) : null);
  }, [filterTags, setFiltersParam]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setFiltersParam(null);
  }, [setFiltersParam]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filtersParam]);

  const handleSort = useCallback((field: TicketSortField) => {
    let newDirection: SortDirection;
    if (sortField === field) {
      newDirection = sortDirection === "asc" ? "desc" : "asc";
    } else {
      newDirection = "desc";
    }
    setSortField(field);
    setSortDirection(newDirection);
    localStorage.setItem(TICKET_SORT_STORAGE_KEY, JSON.stringify({ field, direction: newDirection }));
    setCurrentPage(1);
  }, [sortField, sortDirection]);

  // Get status filters for the query
  const statusFilters = useMemo(() => 
    filterTags.filter(t => t.field === "status").map(t => t.value),
  [filterTags]);

  const queryClient = useQueryClient();
  const { data: tickets, isLoading } = useQuery({
    queryKey: [TICKETS_QUERY_KEY, statusFilters],
    queryFn: async () => {
      // Paginate through all tickets (Supabase default limit is 1000)
      const allData: any[] = [];
      const pageSize = 1000;
      let from = 0;
      let hasMore = true;

      while (hasMore) {
        let queryBuilder = supabase
          .from("tickets")
          .select(
            `
            *,
            events!inner (
              title,
              start_time,
              timezone
            ),
            profiles!tickets_user_id_fkey (
              id,
              first_name,
              last_name,
              email,
              phone
            ),
            refunder:profiles!tickets_refunded_by_fkey (
              id,
              first_name,
              last_name
            )
          `
          )
          .order("created_at", { ascending: false })
          .range(from, from + pageSize - 1);

        // Filter by selected statuses if any
        if (statusFilters.length > 0) {
          queryBuilder = queryBuilder.in("status", statusFilters);
        }
        
        const { data, error } = await queryBuilder;
        if (error) throw error;
        
        if (data && data.length > 0) {
          allData.push(...data);
          from += pageSize;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      return allData;
    },
  });

  const deleteTicket = useCallback(
    async (ticketId: string) => {
      const isAgree = window.confirm(
        "Are you sure you want to delete this ticket?"
      );

      if (!isAgree) return;
      try {
        const { error } = await supabase
          .from("tickets")
          .delete()
          .eq("id", ticketId);

        if (error) throw error;
        queryClient.invalidateQueries({ queryKey: [TICKETS_QUERY_KEY] });
        toast({
          title: "Success",
          description: "Ticket deleted successfully",
        });
      } catch (error) {
        console.error("Error deleting ticket", error);
        toast({
          title: "Error",
          description: "Failed to delete ticket",
        });
        throw error;
      }
    },
    [queryClient]
  );

  const [refundingTicketId, setRefundingTicketId] = useState<string | null>(null);

  const refundTicket = useCallback(
    async (ticketId: string, eventTitle: string, userName: string) => {
      const isAgree = window.confirm(
        `Are you sure you want to refund this ticket for "${eventTitle}" to ${userName}? This will add credits back to their account.`
      );

      if (!isAgree) return;
      
      setRefundingTicketId(ticketId);
      
      try {
        const { data, error } = await supabase.functions.invoke('process-refund', {
          body: { ticketId }
        });

        if (error) throw error;
        
        queryClient.invalidateQueries({ queryKey: [TICKETS_QUERY_KEY] });
        toast({
          title: "Refund Processed",
          description: `${data.creditsRefunded} credits have been added to ${userName}'s account`,
        });
      } catch (error: any) {
        console.error("Error processing refund", error);
        toast({
          title: "Error",
          description: error.message || "Failed to process refund",
          variant: "destructive",
        });
      } finally {
        setRefundingTicketId(null);
      }
    },
    [queryClient]
  );

  const isEmpty = !isLoading && !tickets?.length;

  // Client-side filtering and sorting
  const sortedTickets = useMemo(() => {
    if (!tickets?.length) return [];

    let filtered = [...tickets];

    // Group filters by field for OR logic within same field
    const filtersByField = filterTags.reduce((acc, tag) => {
      if (!acc[tag.field]) acc[tag.field] = [];
      acc[tag.field].push({ value: tag.value.toLowerCase(), operator: tag.operator });
      return acc;
    }, {} as Record<FilterField, { value: string; operator?: FilterOperator }[]>);

    // Apply filters - OR within same field, AND across different fields
    // Status is already filtered server-side, skip here

    if (filtersByField.event?.length) {
      filtered = filtered.filter(t => 
        filtersByField.event.some(f => 
          compareStringWithOperator(t.events?.title || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.name?.length) {
      filtered = filtered.filter(t => {
        const fullName = `${t.profiles?.first_name || ""} ${t.profiles?.last_name || ""}`;
        return filtersByField.name.some(f => 
          compareStringWithOperator(fullName, f.value, f.operator || "contains")
        );
      });
    }

    if (filtersByField.email?.length) {
      filtered = filtered.filter(t => 
        filtersByField.email.some(f => 
          compareStringWithOperator(t.profiles?.email || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.phone?.length) {
      filtered = filtered.filter(t => 
        filtersByField.phone.some(f => 
          compareStringWithOperator(t.profiles?.phone || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.userId?.length) {
      filtered = filtered.filter(t => 
        filtersByField.userId.some(f => 
          compareStringWithOperator(t.user_id || "", f.value, f.operator || "contains")
        )
      );
    }

    if (filtersByField.payment?.length) {
      filtered = filtered.filter(t => 
        filtersByField.payment.some(f => t.payment_method?.toLowerCase() === f.value)
      );
    }

    if (filtersByField.price?.length) {
      filtered = filtered.filter(t => 
        filtersByField.price.some(f => 
          compareWithOperator(t.total_price || 0, parseFloat(f.value), f.operator || "equals")
        )
      );
    }

    if (filtersByField.quantity?.length) {
      filtered = filtered.filter(t => 
        filtersByField.quantity.some(f => 
          compareWithOperator(t.quantity || 0, parseInt(f.value), f.operator || "equals")
        )
      );
    }

    // Sort
    const sorted = filtered.sort((a, b) => {
      let aVal: string | number | null;
      let bVal: string | number | null;

      switch (sortField) {
        case "created_at":
          aVal = a.created_at ? new Date(a.created_at).getTime() : 0;
          bVal = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        case "event":
          aVal = a.events?.title?.toLowerCase() || "";
          bVal = b.events?.title?.toLowerCase() || "";
          break;
        case "guest":
          aVal = `${a.profiles?.first_name || ""} ${a.profiles?.last_name || ""}`.trim().toLowerCase();
          bVal = `${b.profiles?.first_name || ""} ${b.profiles?.last_name || ""}`.trim().toLowerCase();
          break;
        case "total_price":
          aVal = a.total_price || 0;
          bVal = b.total_price || 0;
          break;
        case "quantity":
          aVal = a.quantity || 0;
          bVal = b.quantity || 0;
          break;
        case "status":
          aVal = a.status?.toLowerCase() || "";
          bVal = b.status?.toLowerCase() || "";
          break;
        default:
          return 0;
      }

      if (aVal === bVal) return 0;
      if (sortDirection === "asc") {
        return aVal < bVal ? -1 : 1;
      }
      return aVal > bVal ? -1 : 1;
    });

    return sorted;
  }, [tickets, sortField, sortDirection, filterTags]);

  const totalTickets = sortedTickets.length;
  const totalPages = Math.ceil(totalTickets / TICKETS_PER_PAGE);
  const paginatedTickets = sortedTickets.slice(
    (currentPage - 1) * TICKETS_PER_PAGE,
    currentPage * TICKETS_PER_PAGE
  );

  return {
    tickets: paginatedTickets,
    isLoading,
    isEmpty,
    // Tag-based filters
    filterTags,
    addFilter,
    removeFilter,
    clearAllFilters,
    // Actions
    deleteTicket,
    refundTicket,
    refundingTicketId,
    // Pagination
    currentPage,
    setCurrentPage,
    totalPages,
    totalTickets,
    // Sorting
    sortField,
    sortDirection,
    handleSort,
  };
};
